"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import type { CommitmentLevel } from "@/lib/api";
import {
  LIFE_AREA_COLORS as CANONICAL_LIFE_AREA_COLORS,
  LIFE_AREA_KEYS,
  LIFE_AREA_META,
} from "@/lib/lifeAreas";

// ── Tokens specific to this modal — sourced verbatim from the design
//    handoff so the editorial feel doesn't drift from the spec. They
//    coexist with our existing palette rather than replacing it. ──
const TOKENS = {
  page: "#FFF8F0",
  ink: "#2B2620",
  sub: "#5C544A",
  muted: "#8A7E70",
  field: "#FAF3E7",
  fieldHover: "#F6ECDA",
  line: "rgba(80, 60, 40, 0.10)",
  lineHover: "rgba(80, 60, 40, 0.06)",
  lineActive: "rgba(80, 60, 40, 0.18)",
  primary: "#C97A3D",
  primaryDeep: "#A8612D",
  primarySoft: "rgba(201, 122, 61, 0.09)",
  saveDone: "#2F5A2F",
} as const;

// Use canonical life-area registry so this modal stays in sync with
// every other surface (BalanceList, ScheduleGoalsScreen, QueuePeek,
// PetalLifeWheel, Reflect). See @/lib/lifeAreas.
const AREA_COLORS: Record<string, string> = CANONICAL_LIFE_AREA_COLORS;

const AREA_OPTIONS: Array<{ key: string; label: string }> = LIFE_AREA_KEYS.map(
  (key) => ({ key, label: LIFE_AREA_META[key].label }),
);

const COMMITMENT_OPTIONS: Array<{ value: CommitmentLevel; label: string }> = [
  { value: "must_do", label: "Protected" },
  { value: "planned", label: "Planned" },
  { value: "flexible", label: "Flexible" },
];

const KIND_LABEL: Record<string, string> = {
  task: "Routine",
  break: "Recovery",
  routine: "Routine",
  custom: "Note",
  calendar: "Event",
};

export interface BlockEditPayload {
  title: string;
  description: string;
  startTime: string; // "HH:MM"
  endTime: string;
  commitmentLevel: CommitmentLevel;
  lifeArea: string | null;
  durationDays: number;
}

interface Props {
  open: boolean;
  initial: {
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    commitmentLevel: CommitmentLevel;
    lifeArea: string | null;
    durationDays: number;
    kind?: string | null;
    goalTitle?: string | null;
    rationale?: string | null;
    blockId?: string | null;
  } | null;
  onClose: () => void;
  onSave: (payload: BlockEditPayload) => Promise<void> | void;
  onDelete?: (blockId: string) => Promise<void> | void;
  // Mark the block (and its linked task + goal) done. Provided by the parent
  // only for an incomplete task block; absence hides the control.
  onComplete?: () => Promise<void> | void;
  completing?: boolean;
  testId?: string;
  // Callback so the parent can apply the terracotta source-block ring
  // to the timeline tile while this modal is open. Called with the
  // active block id when open, null when closed.
  onActiveBlockChange?: (blockId: string | null) => void;
}

type SaveBeat = "idle" | "saving" | "saved";

function clampDuration(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.min(30, Math.round(value)));
}

function formatTimePill(value: string): string {
  // "07:05" → "7:05 am"
  if (!value) return "—";
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  const h24 = Number(match[1]);
  const minutes = match[2];
  const meridiem = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${minutes} ${meridiem}`;
}

function parseTimePillInput(raw: string): string | null {
  // Accept "9:30 am", "9:30am", "9:30 AM", "21:30", "9 pm", "9pm",
  // "2130". Returns 24h "HH:MM" or null when unparseable.
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;

  // 12h with optional minutes: "9 am", "9:30 am", "9:30am"
  const m12 = trimmed.match(/^(\d{1,2})(?:[:.](\d{1,2}))?\s*(am|pm)$/);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const minute = m12[2] !== undefined ? parseInt(m12[2], 10) : 0;
    const meridiem = m12[3];
    if (Number.isNaN(h) || Number.isNaN(minute)) return null;
    if (h < 1 || h > 12 || minute < 0 || minute > 59) return null;
    if (meridiem === "am" && h === 12) h = 0;
    if (meridiem === "pm" && h !== 12) h += 12;
    return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }

  // 24h "HH:MM" or "H:MM"
  const m24 = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m24) {
    const h = parseInt(m24[1], 10);
    const minute = parseInt(m24[2], 10);
    if (h < 0 || h > 23 || minute < 0 || minute > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }

  // Compact "2130"
  const compact = trimmed.match(/^(\d{1,2})(\d{2})$/);
  if (compact) {
    const h = parseInt(compact[1], 10);
    const minute = parseInt(compact[2], 10);
    if (h < 0 || h > 23 || minute < 0 || minute > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }

  return null;
}

function diffMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  return eh * 60 + em - (sh * 60 + sm);
}

export default function BlockEditModal({
  open,
  initial,
  onClose,
  onSave,
  onDelete,
  onComplete,
  completing = false,
  testId = "schedule-edit-modal",
  onActiveBlockChange,
}: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [commitmentLevel, setCommitmentLevel] = useState<CommitmentLevel>("planned");
  const [lifeArea, setLifeArea] = useState<string | null>("personal");
  const [durationDays, setDurationDays] = useState(1);
  const [saveBeat, setSaveBeat] = useState<SaveBeat>("idle");
  const [deleting, setDeleting] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate form whenever the modal opens with a fresh block.
  //
  // Important: `initial` is a fresh object literal on every parent
  // render, so depending on it would re-run this effect (and reset
  // saveBeat) every time the parent re-renders — including the
  // re-render the parent does mid-save when it calls setBlocks. That
  // was flipping saveBeat back to "idle" while the save flow was still
  // in flight, making the button look like it never started saving.
  //
  // Key the effect on `open` and the block identity instead, so it
  // fires only when a new block becomes the modal's subject.
  const initialKey = open ? initial?.blockId ?? "__new__" : null;
  useEffect(() => {
    if (open && initial) {
      setTitle(initial.title);
      setDescription(initial.description);
      setStartTime(initial.startTime);
      setEndTime(initial.endTime);
      setCommitmentLevel(initial.commitmentLevel);
      setLifeArea(initial.lifeArea);
      setDurationDays(initial.durationDays);
      setSaveBeat("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKey]);

  // Anchor the source block on the timeline whenever the modal is open.
  useEffect(() => {
    if (!onActiveBlockChange) return;
    if (open && initial?.blockId) {
      onActiveBlockChange(initial.blockId);
      return () => onActiveBlockChange(null);
    }
    onActiveBlockChange(null);
    return undefined;
  }, [onActiveBlockChange, open, initial?.blockId]);

  // Cleanup pending collapse timer on unmount.
  useEffect(() => () => {
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
  }, []);

  const kindLabel = useMemo(() => {
    const key = (initial?.kind ?? "task").toLowerCase();
    return KIND_LABEL[key] ?? "Routine";
  }, [initial?.kind]);

  const areaLabel = useMemo(() => {
    if (!lifeArea) return "Unlinked";
    const opt = AREA_OPTIONS.find((o) => o.key === lifeArea);
    return opt?.label ?? lifeArea;
  }, [lifeArea]);

  const areaSwatch = useMemo(() => {
    if (!lifeArea) return TOKENS.muted;
    return AREA_COLORS[lifeArea] ?? TOKENS.muted;
  }, [lifeArea]);

  const durationMin = diffMinutes(startTime, endTime);
  // Reject end ≤ start so we never persist a negative-duration block,
  // BUT only when both ends parse to real numbers. An unparseable
  // time (initial empty state, mid-typing) shouldn't block save —
  // we just don't know enough to call it invalid yet.
  const timeRangeInvalid = (() => {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return false;
    return eh * 60 + em <= sh * 60 + sm;
  })();

  const handleSave = async () => {
    if (saveBeat !== "idle") return;
    if (timeRangeInvalid) return;
    setSaveBeat("saving");
    // Minimum saving-beat duration so the spinner is always visible —
    // dev-mode persist often resolves in <100ms, faster than the eye
    // can register beat 1. Industry-standard 600ms feels intentional.
    const minSavingBeat = new Promise((resolve) => setTimeout(resolve, 600));
    try {
      await Promise.all([
        onSave({
          title: title.trim() || (initial?.title ?? ""),
          description,
          startTime,
          endTime,
          commitmentLevel,
          lifeArea,
          durationDays,
        }),
        minSavingBeat,
      ]);
      // Beat 2: saved (checkmark draws). After the hold, hand off
      // directly to AnimatePresence's exit so the modal sinks in ONE
      // continuous animation. Previously we had an intermediate
      // "collapse" beat that animated the modal down, then exit fired
      // a second animation that lerped it back up — the user saw it
      // vanish/reappear/vanish. Letting exit own the sink fixes that.
      setSaveBeat("saved");
      collapseTimerRef.current = setTimeout(() => {
        onClose();
      }, 420); // beat 2 hold before exit
    } catch {
      // Error path not designed yet — flagged in handoff.
      setSaveBeat("idle");
    }
  };

  const handleDelete = async () => {
    if (!initial?.blockId || !onDelete || deleting) return;
    setDeleting(true);
    try {
      await onDelete(initial.blockId);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (saveBeat !== "idle" && saveBeat !== "saving") return;
    onClose();
  };

  // ESC + ⌘↵
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
        return;
      }
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void handleSave();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saveBeat]);

  return (
    <AnimatePresence>
      {open && initial && (
        <motion.div
          key="block-edit-overlay"
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, backdropFilter: "blur(3px)" }}
          exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
          transition={{ duration: 0.36, ease: [0.2, 0.8, 0.2, 1] }}
          style={{
            backgroundColor: "rgba(40,28,18,0.32)",
            WebkitBackdropFilter: "blur(3px)",
            backdropFilter: "blur(3px)",
          }}
          onClick={handleCancel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="block-edit-title"
        >
          <motion.div
            key="block-edit-shell"
            className="relative w-full max-w-[560px] overflow-hidden"
            style={{
              background: TOKENS.page,
              color: TOKENS.ink,
              borderRadius: 14,
              boxShadow:
                "0 1px 0 rgba(0,0,0,0.03), 0 30px 60px -20px rgba(40,28,18,0.45), 0 80px 160px -40px rgba(40,28,18,0.35)",
            }}
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={
              saveBeat === "saving"
                ? { opacity: 1, y: -4, scale: 1.005 }
                : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
            }
            // Save flow → drop into the timeline (handoff beat 3).
            // Cancel/ESC → soft shrink. Same prop drives both —
            // AnimatePresence runs only one animation, no double-pose.
            exit={
              saveBeat === "saved"
                ? { opacity: 0, y: 40, scale: 0.82, filter: "blur(2px)" }
                : { opacity: 0, y: 12, scale: 0.96 }
            }
            transition={{
              duration: saveBeat === "saved" ? 0.48 : 0.42,
              delay: saveBeat === "idle" ? 0.06 : 0,
              ease:
                saveBeat === "saved" ? [0.6, 0, 0.4, 1] : [0.2, 0.8, 0.2, 1],
            }}
            onClick={(event) => event.stopPropagation()}
            data-testid={testId}
          >
            {/* Header */}
            <div className="px-7 pt-[22px]">
              <div className="flex items-start justify-between gap-3">
                <StaggeredItem delay={140}>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-[7px] w-[7px] rounded-full transition-colors"
                      style={{ background: areaSwatch }}
                      aria-hidden="true"
                    />
                    <p
                      className="text-[10.5px] font-bold uppercase"
                      style={{ color: TOKENS.primary, letterSpacing: "1.4px" }}
                    >
                      {kindLabel} · {areaLabel}
                    </p>
                  </div>
                </StaggeredItem>
                <button
                  type="button"
                  onClick={handleCancel}
                  aria-label="Close"
                  className="grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-[rgba(80,60,40,0.06)]"
                  style={{ color: TOKENS.ink }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <StaggeredItem delay={180}>
                <input
                  id="block-edit-title"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Block title"
                  className="mt-2 w-full border-0 bg-transparent px-0 py-1.5 font-serif outline-none transition-colors focus:border-b"
                  style={{
                    fontSize: 30,
                    fontWeight: 500,
                    letterSpacing: "-0.6px",
                    lineHeight: 1.1,
                    color: TOKENS.ink,
                    borderBottom: `1px solid transparent`,
                  }}
                  onFocus={(event) =>
                    (event.currentTarget.style.borderBottom = `1px solid ${TOKENS.lineActive}`)
                  }
                  onBlur={(event) => (event.currentTarget.style.borderBottom = "1px solid transparent")}
                />
              </StaggeredItem>
              <StaggeredItem delay={200}>
                <div className="mt-4 h-px" style={{ background: TOKENS.line }} />
              </StaggeredItem>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto px-7 pt-2">
              <FieldRow icon={<NotesIcon />} label="Notes" delay={220}>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Add a note for this block…"
                  rows={3}
                  className="w-full border-0 bg-transparent px-0 py-1 outline-none italic placeholder-[color:var(--placeholder)] focus:not-italic"
                  style={{
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: TOKENS.ink,
                    fontStyle: description ? "normal" : "italic",
                    minHeight: "calc(3 * 1.5em + 8px)",
                    resize: "vertical",
                    ["--placeholder" as string]: TOKENS.muted,
                  } as React.CSSProperties}
                />
              </FieldRow>

              <FieldRow icon={<ClockIcon />} label="When" delay={260}>
                <div className="flex flex-wrap items-center gap-2">
                  <TimePill
                    value={startTime}
                    onChange={setStartTime}
                    invalid={timeRangeInvalid}
                    ariaLabel="Start time"
                  />
                  <span style={{ color: TOKENS.muted }}>→</span>
                  <TimePill
                    value={endTime}
                    onChange={setEndTime}
                    invalid={timeRangeInvalid}
                    ariaLabel="End time"
                  />
                  <span
                    className="text-[10.5px] font-bold uppercase"
                    style={{
                      color: timeRangeInvalid ? "#B23A2B" : TOKENS.muted,
                      letterSpacing: "1.4px",
                    }}
                  >
                    {timeRangeInvalid
                      ? "End must be after start"
                      : `${durationMin} min`}
                  </span>
                </div>
              </FieldRow>

              {(initial.rationale || initial.goalTitle) && (
                <FieldRow icon={<SunIcon />} label="Why" delay={300}>
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0.96 }}
                    animate={{ opacity: 1, scaleX: [0.96, 1.01, 1] }}
                    transition={{ duration: 0.6, delay: 0.38, ease: [0.2, 0.8, 0.2, 1] }}
                    className="rounded-md px-3.5 py-3"
                    style={{
                      background: TOKENS.primarySoft,
                      borderLeft: `2px solid ${TOKENS.primary}`,
                      transformOrigin: "left",
                    }}
                  >
                    <p
                      className="text-[9.5px] font-bold uppercase"
                      style={{ color: TOKENS.primaryDeep, letterSpacing: "1.4px" }}
                    >
                      From your goals
                    </p>
                    <p
                      className="mt-1 font-serif"
                      style={{ fontSize: 15, lineHeight: 1.4, letterSpacing: "-0.1px", color: TOKENS.ink }}
                    >
                      {sanitizeSurfaceTitle(initial.rationale ?? `Anchored to ${initial.goalTitle}.`)}
                    </p>
                  </motion.div>
                </FieldRow>
              )}

              <FieldRow icon={<BoltIcon />} label="Commit" delay={340}>
                <div
                  className="inline-flex rounded-full p-[3px]"
                  style={{ background: TOKENS.field }}
                >
                  {COMMITMENT_OPTIONS.map((opt) => {
                    const active = commitmentLevel === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCommitmentLevel(opt.value)}
                        className="rounded-full transition-colors"
                        style={{
                          padding: "7px 14px",
                          fontSize: 12,
                          letterSpacing: "0.1px",
                          fontWeight: active ? 600 : 500,
                          color: active ? TOKENS.ink : TOKENS.sub,
                          background: active ? "white" : "transparent",
                          border: active ? `1px solid ${TOKENS.line}` : "1px solid transparent",
                          boxShadow: active ? "0 1px 0 rgba(0,0,0,0.03)" : "none",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </FieldRow>

              <FieldRow icon={<RepeatIcon />} label="Repeats" delay={380}>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-[10.5px] font-bold uppercase"
                    style={{ color: TOKENS.muted, letterSpacing: "1.4px" }}
                  >
                    Every
                  </span>
                  <Stepper value={durationDays} onChange={(v) => setDurationDays(clampDuration(v))} />
                  <span
                    className="text-[10.5px] font-bold uppercase"
                    style={{ color: TOKENS.muted, letterSpacing: "1.4px" }}
                  >
                    {durationDays === 1 ? "day" : "days"}
                  </span>
                </div>
              </FieldRow>

              <FieldRow icon={<GlobeIcon />} label="Area" delay={420} bordered={false}>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                  {AREA_OPTIONS.map((area) => {
                    const active = lifeArea === area.key;
                    return (
                      <button
                        key={area.key}
                        type="button"
                        onClick={() => setLifeArea(area.key)}
                        className="inline-flex items-center gap-1.5 rounded-full transition-colors"
                        style={{
                          padding: "7px 12px",
                          fontSize: 12,
                          fontWeight: active ? 600 : 500,
                          background: active ? TOKENS.ink : "transparent",
                          color: active ? TOKENS.page : TOKENS.sub,
                          border: active ? "1px solid transparent" : `1px solid ${TOKENS.line}`,
                        }}
                      >
                        <span
                          className="inline-block h-[7px] w-[7px] rounded-full"
                          style={{ background: AREA_COLORS[area.key] }}
                          aria-hidden="true"
                        />
                        {area.label}
                      </button>
                    );
                  })}
                </div>
              </FieldRow>
            </div>

            {/* Footer */}
            <StaggeredItem delay={460}>
              <div
                className="flex items-center justify-between gap-3 px-7 py-4"
                style={{ borderTop: `1px solid ${TOKENS.line}` }}
              >
                {initial.blockId && onDelete ? (
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={deleting || saveBeat !== "idle"}
                    className="inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    style={{ color: TOKENS.muted, fontSize: 13 }}
                    onMouseEnter={(event) => (event.currentTarget.style.color = "#B23A2B")}
                    onMouseLeave={(event) => (event.currentTarget.style.color = TOKENS.muted)}
                  >
                    <TrashIcon />
                    {deleting ? "Deleting…" : "Delete block"}
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  {onComplete ? (
                    <button
                      type="button"
                      onClick={() => void onComplete()}
                      disabled={completing || saveBeat !== "idle"}
                      aria-label="Mark done"
                      data-testid="block-edit-complete"
                      className="inline-flex items-center gap-1.5 rounded-full transition-colors disabled:opacity-50"
                      style={{
                        padding: "9px 18px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#FFFFFF",
                        // Forest-med — the calm primary; never red (ADHD palette).
                        background: "#3E5E3A",
                        border: "1px solid #3E5E3A",
                      }}
                    >
                      {completing ? "Marking…" : "✓ Mark done"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saveBeat !== "idle"}
                    className="rounded-full transition-colors disabled:opacity-50"
                    style={{
                      padding: "9px 18px",
                      fontSize: 13,
                      fontWeight: 500,
                      color: TOKENS.ink,
                      border: `1px solid ${TOKENS.line}`,
                      background: "transparent",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={saveBeat !== "idle" || timeRangeInvalid}
                    aria-disabled={saveBeat !== "idle" || timeRangeInvalid}
                    // Strict UAT 2026-05-24: explicit aria-label so
                    // the button's accessible name is just "Save"
                    // (no ⌘↵ kbd glyph), matching the runner's
                    // `getByRole("button", { name: "Save", exact: true })`.
                    aria-label={
                      saveBeat === "saving"
                        ? "Saving"
                        : saveBeat === "saved"
                          ? "Saved"
                          : "Save"
                    }
                    data-testid="block-edit-save"
                    title={
                      timeRangeInvalid
                        ? "End time must be after start time"
                        : undefined
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    style={{
                      minWidth: 110,
                      padding: "9px 22px",
                      fontSize: 13,
                      fontWeight: 500,
                      color: TOKENS.page,
                      background:
                        saveBeat === "saved"
                          ? TOKENS.saveDone
                          : saveBeat === "saving"
                            ? TOKENS.primaryDeep
                            : TOKENS.ink,
                    }}
                  >
                    {saveBeat === "saving" && <Spinner />}
                    {(saveBeat === "saved") && <CheckSvg />}
                    <span>
                      {saveBeat === "saving"
                        ? "Saving"
                        : saveBeat === "saved"
                          ? "Saved"
                          : "Save"}
                    </span>
                    {saveBeat === "idle" && (
                      <kbd
                        className="inline-flex items-center justify-center rounded"
                        style={{
                          padding: "1px 5px",
                          fontSize: 10,
                          background: "rgba(255,255,255,0.18)",
                          color: "white",
                          fontFamily: "inherit",
                          letterSpacing: 0,
                        }}
                      >
                        ⌘↵
                      </kbd>
                    )}
                  </button>
                </div>
              </div>
            </StaggeredItem>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Inner pieces ────────────────────────────────────────────────────

function StaggeredItem({ delay, children }: { delay: number; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: delay / 1000, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}

function FieldRow({
  icon,
  label,
  children,
  delay,
  bordered = true,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  delay: number;
  bordered?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: delay / 1000, ease: [0.2, 0.8, 0.2, 1] }}
      className="grid items-start gap-4 py-4"
      style={{
        gridTemplateColumns: "96px 1fr",
        borderBottom: bordered ? `1px solid ${TOKENS.line}` : "none",
      }}
    >
      <div className="flex items-center gap-2 pt-1.5">
        <span style={{ color: TOKENS.muted, opacity: 0.85 }}>{icon}</span>
        <span
          className="text-[10.5px] font-bold uppercase"
          style={{ color: TOKENS.muted, letterSpacing: "1.4px" }}
        >
          {label}
        </span>
      </div>
      <div>{children}</div>
    </motion.div>
  );
}

function TimePill({
  value,
  onChange,
  invalid = false,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  ariaLabel?: string;
}) {
  // Free-text time editor — the user can type "9:30 am", "21:30",
  // "2130", "9 pm" etc. We keep an internal `draft` string so they
  // can mid-type without us snapping the value, then on blur (or
  // Enter) we parse it. Unparseable input rolls back to the last
  // valid value rather than silently saving garbage.
  const [draft, setDraft] = useState(formatTimePill(value));

  useEffect(() => {
    setDraft(formatTimePill(value));
  }, [value]);

  const commit = () => {
    const parsed = parseTimePillInput(draft);
    if (parsed && parsed !== value) {
      onChange(parsed);
      return;
    }
    if (!parsed) {
      // Invalid input — revert to the last known good value.
      setDraft(formatTimePill(value));
    }
  };

  return (
    <span
      className="relative inline-flex items-center gap-1.5 rounded-full"
      style={{
        background: invalid ? "rgba(178, 58, 43, 0.08)" : TOKENS.field,
        padding: "7px 14px",
        fontSize: 13,
        fontWeight: 500,
        fontVariantNumeric: "tabular-nums",
        color: invalid ? "#B23A2B" : TOKENS.ink,
        border: invalid
          ? "1px solid rgba(178, 58, 43, 0.45)"
          : "1px solid transparent",
        transition:
          "background-color 200ms ease, border-color 200ms ease, color 200ms ease",
      }}
    >
      <ClockGlyphSmall />
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          // Strict UAT 2026-05-24 — commit on input when the draft
          // already parses cleanly so programmatic `.fill("20:00")`
          // propagates without waiting for blur. Users typing
          // partials ("9:" / "9:3") still benefit from the
          // commit-on-blur behaviour below because the parse will
          // return null and the draft sticks.
          const parsed = parseTimePillInput(next);
          if (parsed && parsed !== value) {
            onChange(parsed);
          }
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            setDraft(formatTimePill(value));
            event.currentTarget.blur();
          }
        }}
        aria-label={ariaLabel ?? "Time"}
        aria-invalid={invalid || undefined}
        className="bg-transparent outline-none"
        style={{
          width: "5.5ch",
          color: "inherit",
          fontVariantNumeric: "tabular-nums",
        }}
      />
    </span>
  );
}

function Stepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div
      className="inline-flex items-center rounded-full"
      style={{ background: TOKENS.field, padding: "2px 4px", height: 30 }}
    >
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        className="grid h-[26px] w-[26px] place-items-center rounded-full"
        style={{ color: TOKENS.ink, background: "transparent" }}
        aria-label="Decrease"
      >
        −
      </button>
      <span
        className="px-2 text-center"
        style={{ minWidth: 22, fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="grid h-[26px] w-[26px] place-items-center rounded-full"
        style={{ color: TOKENS.ink, background: "transparent" }}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block animate-spin rounded-full"
      style={{
        width: 14,
        height: 14,
        border: "1.6px solid rgba(255,255,255,0.35)",
        borderTopColor: "white",
      }}
    />
  );
}

function CheckSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <motion.path
        d="M2.5 7.5 L6 11 L11.5 4"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      />
    </svg>
  );
}

// ── Inline icons (line-style, 24×24, stroke 1.8) ───────────────────

function NotesIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="14" y2="18" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </svg>
  );
}
function ClockGlyphSmall() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </svg>
  );
}
function SunIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 4 14 11 14 11 22 20 10 13 10 13 2" />
    </svg>
  );
}
function RepeatIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}
function GlobeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
